import type {
  FinanceConfidenceReason,
  FinanceConfidenceReasonCode,
  FinanceDataReliability,
  FinanceDepartmentAverageRates,
  FinancePresentationMode,
  Task,
  TaskFinanceEvent,
  TaskFinanceTrigger,
  TaskFinancialStage,
  TaskFinancialStageSnapshot,
  TaskFinancialState,
  TimeLog,
} from "../models/production";
import type { AssetRateCard, Person, ProjectSetup, RateCard } from "../types";

export const MINUTES_PER_DAY = 480;
const COMPLETION_INCOMPLETE_LOG_THRESHOLD = 0.25;
const STALE_TIMELOG_DAYS = 14;
const FINAL_PROGRESS_CODES = new Set(["fin", "final", "complete", "completed", "dlvr", "delivered"]);
const IN_PROGRESS_CODES = new Set(["ip", "in_progress", "in progress", "wip", "wtg", "rdy"]);
const STAGE_ORDER: TaskFinancialStage[] = [
  "initial_bid_forecast",
  "assigned_artist_forecast",
  "in_progress_on_target",
  "in_progress_over_schedule",
  "final_actual",
];

export interface TaskFinanceStageContext {
  setup?: Pick<ProjectSetup, "currency" | "financeCurrency" | "financeDepartmentAverageRates">;
  people?: Array<Pick<Person, "id" | "dailyRate">>;
  timeLogs?: TimeLog[];
  shotRates?: Partial<RateCard> | null;
  assetRates?: Partial<AssetRateCard> | null;
  departmentAverageRates?: FinanceDepartmentAverageRates | Record<string, number>;
  now?: string;
}

export interface TaskFinanceStageResolution {
  nextState: TaskFinancialState;
  events: TaskFinanceEvent[];
  alertsRaised: TaskFinanceEvent[];
  stageChanged: boolean;
  shouldRecomputeRollups: boolean;
}

export interface TaskFinanceRollupRecalculationPlan {
  shouldRecompute: boolean;
  projectIds: string[];
  episodeIds: string[];
  sequenceIds: string[];
  shotIds: string[];
  assetIds: string[];
}

function roundCurrency(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 100) / 100;
}

function roundDays(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 1000) / 1000;
}

function roundMargin(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 10) / 10;
}

function differenceInDays(laterIso?: string, earlierIso?: string) {
  if (!laterIso || !earlierIso) return undefined;
  const later = new Date(laterIso).getTime();
  const earlier = new Date(earlierIso).getTime();
  if (!Number.isFinite(later) || !Number.isFinite(earlier)) return undefined;
  return (later - earlier) / (1000 * 60 * 60 * 24);
}

function makeConfidenceReason(code: FinanceConfidenceReasonCode): FinanceConfidenceReason {
  switch (code) {
    case "department_average_only":
      return {
        code,
        label: "Department-average forecast",
        helperText: "No artist is assigned yet, so labour cost is forecast from the department average daily rate and the original bid allocation.",
      };
    case "assigned_artist_no_timelogs":
      return {
        code,
        label: "Assigned artist forecast",
        helperText: "An artist is assigned, but no timelogs exist yet. Forecast labour cost uses the assigned artist rate and the bid allocation.",
      };
    case "timelogs_partial":
      return {
        code,
        label: "Partial timelog coverage",
        helperText: "Timelogs have started, but the task still depends on remaining bid-time assumptions to forecast the likely final labour cost.",
      };
    case "logging_gap_assumed":
      return {
        code,
        label: "Logging gap fallback",
        helperText: "Live logging appears to have stopped before the task naturally finished, so labour cost falls back to the full bid allocation at the assigned artist rate rather than treating the partial logs as complete.",
      };
    case "timelogs_missing_on_complete":
      return {
        code,
        label: "Missing completed timelogs",
        helperText: "The task is complete, but no meaningful timelogs were found. Actual labour cost falls back to the bid allocation.",
      };
    case "timelogs_incomplete_on_complete":
      return {
        code,
        label: "Incomplete completed timelogs",
        helperText: "The task is complete, but logged effort looks too low to trust fully. Actual labour cost falls back to bid assumptions.",
      };
    case "timelog_stale":
      return {
        code,
        label: "Stale timelog activity",
        helperText: "Timelog activity looks stale relative to the current task state, so the live financial view should be treated with caution.",
      };
    case "missing_rate_data":
      return {
        code,
        label: "Missing rate data",
        helperText: "Rate coverage is incomplete, so some labour cost values are assumption-driven rather than fully evidence-based.",
      };
  }
}

function normalizeDepartmentKey(value?: string | null) {
  return String(value || "").trim();
}

function getDefaultDepartmentAverageRate(task: Task) {
  const key = normalizeDepartmentKey(task.taskType).toLowerCase();
  if (["supervisor", "supervision", "production", "editorial"].includes(key)) {
    return 300;
  }
  return 250;
}

function getDepartmentAverageRate(task: Task, context: TaskFinanceStageContext) {
  const inlineRates = context.setup?.financeDepartmentAverageRates || {};
  const rawDepartmentAverageRates = context.departmentAverageRates;
  const wrappedRates =
    rawDepartmentAverageRates &&
    typeof rawDepartmentAverageRates === "object" &&
    "ratesByDepartment" in rawDepartmentAverageRates &&
    typeof (rawDepartmentAverageRates as { ratesByDepartment?: unknown }).ratesByDepartment === "object"
      ? ((rawDepartmentAverageRates as { ratesByDepartment?: Record<string, number> }).ratesByDepartment || {})
      : null;
  const configuredRates: Record<string, number> = wrappedRates || ((rawDepartmentAverageRates as Record<string, number> | undefined) || {});

  const key = normalizeDepartmentKey(task.taskType);
  const mergedRates = {
    ...configuredRates,
    ...inlineRates,
  };
  const configuredRate = mergedRates[key];
  if (typeof configuredRate === "number" && Number.isFinite(configuredRate)) {
    return configuredRate;
  }
  return getDefaultDepartmentAverageRate(task);
}

function getTaskSellRate(task: Task, context: TaskFinanceStageContext) {
  const key = normalizeDepartmentKey(task.taskType);
  if (!key) return undefined;
  if (task.parentEntityType === "asset") {
    const rate = context.assetRates?.[key as keyof AssetRateCard];
    return typeof rate === "number" && Number.isFinite(rate) ? rate : undefined;
  }
  const rate = context.shotRates?.[key as keyof RateCard];
  return typeof rate === "number" && Number.isFinite(rate) ? rate : undefined;
}

function getAssignedArtistDailyRate(task: Task, people: TaskFinanceStageContext["people"]) {
  const assignedPeople = (people || []).filter((person) => (task.assignedArtistIds || []).includes(person.id));
  const knownRates = assignedPeople
    .map((person) => person.dailyRate)
    .filter((rate): rate is number => typeof rate === "number" && Number.isFinite(rate));

  if (knownRates.length === 0) return undefined;
  if (task.primaryArtistId) {
    const primaryRate = assignedPeople.find((person) => person.id === task.primaryArtistId)?.dailyRate;
    if (typeof primaryRate === "number" && Number.isFinite(primaryRate)) return primaryRate;
  }
  return knownRates.reduce((sum, rate) => sum + rate, 0) / knownRates.length;
}

function getTaskBidDays(task: Task) {
  if (typeof task.finance?.bidTimeAllocationDays === "number" && Number.isFinite(task.finance.bidTimeAllocationDays)) {
    return task.finance.bidTimeAllocationDays;
  }
  if (typeof task.bidHours === "number" && Number.isFinite(task.bidHours)) {
    return task.bidHours;
  }
  if (typeof task.finance?.bidTimeAllocationMinutes === "number" && Number.isFinite(task.finance.bidTimeAllocationMinutes)) {
    return task.finance.bidTimeAllocationMinutes / MINUTES_PER_DAY;
  }
  return 0;
}

function getTaskBidMinutes(task: Task) {
  if (typeof task.finance?.bidTimeAllocationMinutes === "number" && Number.isFinite(task.finance.bidTimeAllocationMinutes)) {
    return task.finance.bidTimeAllocationMinutes;
  }
  return Math.round(getTaskBidDays(task) * MINUTES_PER_DAY);
}

function getTaskBidValueInMoney(task: Task, context: TaskFinanceStageContext) {
  if (typeof task.finance?.taskBidValueInMoney === "number" && Number.isFinite(task.finance.taskBidValueInMoney)) {
    return task.finance.taskBidValueInMoney;
  }
  const sellRate = getTaskSellRate(task, context);
  const bidDays = getTaskBidDays(task);
  if (typeof sellRate === "number" && bidDays > 0) {
    return sellRate * bidDays;
  }
  return undefined;
}

function getTaskTimeLogs(task: Task, timeLogs: TimeLog[] = []) {
  return timeLogs.filter((timeLog) => timeLog.taskId === task.id);
}

function getActualLoggedMinutes(task: Task, timeLogs: TimeLog[] = []) {
  return getTaskTimeLogs(task, timeLogs).reduce((sum, timeLog) => sum + (timeLog.minutes || 0), 0);
}

function getActualLabourCost(task: Task, context: TaskFinanceStageContext) {
  const logs = getTaskTimeLogs(task, context.timeLogs);
  if (logs.length === 0) return undefined;

  const peopleById = new Map((context.people || []).map((person) => [person.id, person]));
  const fallbackDailyRate = getDepartmentAverageRate(task, context);
  let derivedCost = 0;
  let canDeriveAllLogs = true;

  for (const timeLog of logs) {
    const personDailyRate = peopleById.get(timeLog.personId)?.dailyRate;
    if (typeof personDailyRate === "number" && Number.isFinite(personDailyRate)) {
      derivedCost += (timeLog.minutes / MINUTES_PER_DAY) * personDailyRate;
      continue;
    }

    // The five-stage lifecycle stays app-led: once work starts we cost live effort
    // from linked people first, and only fall back to the same baseline rate used
    // at stage 1 when historic / ex-staff users are still unmatched locally.
    if (typeof fallbackDailyRate === "number" && Number.isFinite(fallbackDailyRate)) {
      derivedCost += (timeLog.minutes / MINUTES_PER_DAY) * fallbackDailyRate;
      continue;
    }

    canDeriveAllLogs = false;
    break;
  }

  return canDeriveAllLogs ? derivedCost : undefined;
}

function getRateForForecast(task: Task, departmentAverageDailyRate?: number, assignedArtistDailyRate?: number) {
  if ((task.assignedArtistIds || []).length > 0 && typeof assignedArtistDailyRate === "number" && Number.isFinite(assignedArtistDailyRate)) {
    return assignedArtistDailyRate;
  }
  return departmentAverageDailyRate;
}

function isTaskComplete(task: Task) {
  const progressCode = String(task.progressStatus || "").trim().toLowerCase();
  return FINAL_PROGRESS_CODES.has(progressCode);
}

function isTaskInProgress(task: Task) {
  const progressCode = String(task.progressStatus || "").trim().toLowerCase();
  return IN_PROGRESS_CODES.has(progressCode);
}

/**
 * Deterministic stage resolver.
 * The task always starts as a bid forecast, moves to assigned-artist forecast when staffing exists,
 * becomes in-progress once work starts or timelogs appear, flips to over-schedule once logged time
 * exceeds the bid allowance, and finally locks to final actual when the task completes.
 */
export function resolveTaskFinancialStage(task: Task, actualLoggedMinutes: number, assignedArtistDailyRate?: number): TaskFinancialStage {
  const hasAssignedArtists = (task.assignedArtistIds || []).length > 0;
  const bidMinutes = getTaskBidMinutes(task);
  if (isTaskComplete(task)) return "final_actual";
  if (actualLoggedMinutes > 0 || isTaskInProgress(task)) {
    return bidMinutes > 0 && actualLoggedMinutes > bidMinutes ? "in_progress_over_schedule" : "in_progress_on_target";
  }
  if (hasAssignedArtists || (typeof assignedArtistDailyRate === "number" && Number.isFinite(assignedArtistDailyRate))) {
    return "assigned_artist_forecast";
  }
  return "initial_bid_forecast";
}

function buildStageSnapshots(args: {
  taskBidValueInMoney?: number;
  bidDays: number;
  departmentAverageDailyRate?: number;
  assignedArtistDailyRate?: number;
  actualLoggedDays: number;
  actualLabourCost?: number;
}) {
  const { taskBidValueInMoney, bidDays, departmentAverageDailyRate, assignedArtistDailyRate, actualLoggedDays, actualLabourCost } = args;
  const snapshots: TaskFinancialStageSnapshot[] = [];
  const pushSnapshot = (stage: TaskFinancialStage, labourCost?: number, note?: string) => {
    const grossProfit =
      typeof taskBidValueInMoney === "number" && typeof labourCost === "number"
        ? taskBidValueInMoney - labourCost
        : undefined;
    const grossMarginPct =
      typeof grossProfit === "number" && typeof taskBidValueInMoney === "number" && taskBidValueInMoney > 0
        ? (grossProfit / taskBidValueInMoney) * 100
        : undefined;

    snapshots.push({
      stage,
      labourCost: roundCurrency(labourCost),
      grossProfit: roundCurrency(grossProfit),
      grossMarginPct: roundMargin(grossMarginPct),
      note,
    });
  };

  // Stage 1: initial bid-based forecast using department average labour assumption.
  pushSnapshot(
    "initial_bid_forecast",
    typeof departmentAverageDailyRate === "number" ? departmentAverageDailyRate * bidDays : undefined,
    typeof departmentAverageDailyRate === "number" ? "Department-average labour assumption" : "Missing department average rate"
  );

  // Stage 2: forecast updates when an actual artist assignment gives us a truer labour rate.
  pushSnapshot(
    "assigned_artist_forecast",
    typeof assignedArtistDailyRate === "number" ? assignedArtistDailyRate * bidDays : undefined,
    typeof assignedArtistDailyRate === "number" ? "Assigned artist rate replaced department-average forecast" : "No assigned artist rate available"
  );

  // Stage 3: work has started but remains inside the original bid time allowance.
  pushSnapshot(
    "in_progress_on_target",
    typeof actualLabourCost === "number"
      ? actualLabourCost
      : typeof assignedArtistDailyRate === "number"
        ? assignedArtistDailyRate * actualLoggedDays
        : undefined,
    "Work started and remains within bid time allowance"
  );

  // Stage 4: logged time has now exceeded the bid allowance.
  pushSnapshot(
    "in_progress_over_schedule",
    typeof actualLabourCost === "number"
      ? actualLabourCost
      : typeof assignedArtistDailyRate === "number"
        ? assignedArtistDailyRate * actualLoggedDays
        : undefined,
    "Logged time has exceeded the original bid allowance"
  );

  // Stage 5: task is complete, so actual labour cost and GP are treated as final.
  pushSnapshot("final_actual", actualLabourCost, "Task is complete and actual labour cost is fixed");

  return snapshots;
}

function getSnapshotForStage(stageSnapshots: TaskFinancialStageSnapshot[], stage: TaskFinancialStage) {
  return stageSnapshots.find((snapshot) => snapshot.stage === stage);
}

function getPreviousStage(stage: TaskFinancialStage) {
  const stageIndex = STAGE_ORDER.indexOf(stage);
  if (stageIndex <= 0) return undefined;
  return STAGE_ORDER[stageIndex - 1];
}

function getForecastPresentationMode(task: Task, stage: TaskFinancialStage, actualLoggedMinutes: number): FinancePresentationMode {
  if (stage === "final_actual") return "final_actual";
  if (stage === "in_progress_on_target" || stage === "in_progress_over_schedule" || actualLoggedMinutes > 0) {
    return "blended_live";
  }
  return "forecast";
}

function getActualPresentationMode(stage: TaskFinancialStage, fallbackActualsUsed: boolean): FinancePresentationMode {
  if (stage === "final_actual" && fallbackActualsUsed) return "assumption_driven_actual";
  if (stage === "final_actual") return "final_actual";
  return "blended_live";
}

function buildPresentationText(mode: FinancePresentationMode) {
  switch (mode) {
    case "forecast":
      return {
        label: "Forecast",
        helperText: "This figure is still assumption-driven and based on bid allocation and known rate inputs.",
      };
    case "blended_live":
      return {
        label: "Blended Live",
        helperText: "This figure blends actual logged effort so far with remaining bid-time assumptions where work is still underway.",
      };
    case "final_actual":
      return {
        label: "Final Actual",
        helperText: "This figure is based on completed work and actual logged effort.",
      };
    case "assumption_driven_actual":
      return {
        label: "Fallback Actual",
        helperText: "This task is complete, but timelog coverage is missing or incomplete, so the actual figure falls back to bid assumptions.",
      };
  }
}

function buildBaseTaskFinancialState(task: Task, context: TaskFinanceStageContext, currentState?: TaskFinancialState) {
  const bidDays = getTaskBidDays(task);
  const bidMinutes = getTaskBidMinutes(task);
  const taskBidValueInMoney = getTaskBidValueInMoney(task, context);
  const departmentAverageDailyRate = getDepartmentAverageRate(task, context);
  const assignedArtistDailyRate = getAssignedArtistDailyRate(task, context.people);
  const actualLoggedMinutes = getActualLoggedMinutes(task, context.timeLogs);
  const actualLoggedDays = actualLoggedMinutes / MINUTES_PER_DAY;
  const derivedActualLabourCost = getActualLabourCost(task, context);
  const financialStage = resolveTaskFinancialStage(task, actualLoggedMinutes, assignedArtistDailyRate);
  const fallbackRate = getRateForForecast(task, departmentAverageDailyRate, assignedArtistDailyRate);
  const bidDaysRemaining = Math.max(0, bidDays - actualLoggedDays);
  const liveForecastLabourCost =
    typeof derivedActualLabourCost === "number"
      ? derivedActualLabourCost + (typeof fallbackRate === "number" ? bidDaysRemaining * fallbackRate : 0)
      : typeof fallbackRate === "number" && actualLoggedDays > 0
        ? actualLoggedDays * fallbackRate + bidDaysRemaining * fallbackRate
        : undefined;
  const completionLoggedRatio = bidMinutes > 0 ? actualLoggedMinutes / bidMinutes : undefined;
  const complete = isTaskComplete(task);
  const hasMeaningfulTimelogs = actualLoggedMinutes > 0;
  const completionTimelogsIncomplete =
    complete &&
    (actualLoggedMinutes === 0 ||
      (typeof completionLoggedRatio === "number" && completionLoggedRatio > 0 && completionLoggedRatio < COMPLETION_INCOMPLETE_LOG_THRESHOLD));
  const fallbackActualLabourCost =
    complete && completionTimelogsIncomplete && typeof fallbackRate === "number" ? fallbackRate * bidDays : undefined;
  const actualLabourCost = typeof derivedActualLabourCost === "number" && !(complete && completionTimelogsIncomplete) ? derivedActualLabourCost : fallbackActualLabourCost;
  const timelogStalenessDays = differenceInDays(context.now || new Date().toISOString(), task.lastTimelogAt);
  const staleTimelogCoverage =
    (financialStage === "in_progress_on_target" || financialStage === "in_progress_over_schedule") &&
    typeof timelogStalenessDays === "number" &&
    timelogStalenessDays > STALE_TIMELOG_DAYS;
  const liveLoggingGap =
    !complete &&
    staleTimelogCoverage &&
    hasMeaningfulTimelogs &&
    bidMinutes > 0 &&
    actualLoggedMinutes > 0 &&
    actualLoggedMinutes < bidMinutes;
  const loggingGapFallbackLabourCost =
    liveLoggingGap && typeof fallbackRate === "number" ? fallbackRate * bidDays : undefined;
  const stageSnapshots = buildStageSnapshots({
    taskBidValueInMoney,
    bidDays,
    departmentAverageDailyRate,
    assignedArtistDailyRate,
    actualLoggedDays,
    actualLabourCost:
      typeof loggingGapFallbackLabourCost === "number"
        ? loggingGapFallbackLabourCost
        : typeof actualLabourCost === "number"
          ? actualLabourCost
          : liveForecastLabourCost,
  });
  const initialSnapshot = getSnapshotForStage(stageSnapshots, "initial_bid_forecast");
  const currentSnapshot = getSnapshotForStage(stageSnapshots, financialStage);
  const assignedSnapshot = getSnapshotForStage(stageSnapshots, "assigned_artist_forecast");
  const previousStage = getPreviousStage(financialStage);
  const previousSnapshot = previousStage ? getSnapshotForStage(stageSnapshots, previousStage) : undefined;
  const currentComparableLabourCost =
    financialStage === "initial_bid_forecast"
      ? initialSnapshot?.labourCost
      : financialStage === "assigned_artist_forecast"
        ? currentSnapshot?.labourCost
        : typeof loggingGapFallbackLabourCost === "number"
          ? loggingGapFallbackLabourCost
          : actualLabourCost;

  const varianceVsBidMinutes = actualLoggedMinutes - bidMinutes;
  const varianceVsBidCost =
    typeof currentComparableLabourCost === "number" && typeof initialSnapshot?.labourCost === "number"
      ? currentComparableLabourCost - initialSnapshot.labourCost
      : undefined;
  const marginDeltaFromPreviousStage =
    typeof currentSnapshot?.grossMarginPct === "number" && typeof previousSnapshot?.grossMarginPct === "number"
      ? currentSnapshot.grossMarginPct - previousSnapshot.grossMarginPct
      : undefined;

  const missingRateCoverage =
    ((task.assignedArtistIds || []).length > 0 && typeof assignedArtistDailyRate !== "number") ||
    ((task.assignedArtistIds || []).length === 0 && typeof departmentAverageDailyRate !== "number");
  const missingTimelogCoverage =
    (financialStage === "in_progress_on_target" || financialStage === "in_progress_over_schedule" || financialStage === "final_actual") &&
    !hasMeaningfulTimelogs;
  const confidenceReasonCodes = new Set<FinanceConfidenceReasonCode>();

  if ((task.assignedArtistIds || []).length === 0) {
    confidenceReasonCodes.add("department_average_only");
  } else if (!hasMeaningfulTimelogs) {
    confidenceReasonCodes.add("assigned_artist_no_timelogs");
  }

  if ((financialStage === "in_progress_on_target" || financialStage === "in_progress_over_schedule") && hasMeaningfulTimelogs && actualLoggedMinutes < bidMinutes) {
    confidenceReasonCodes.add("timelogs_partial");
  }
  if (liveLoggingGap) {
    confidenceReasonCodes.add("logging_gap_assumed");
  }
  if (complete && actualLoggedMinutes === 0) {
    confidenceReasonCodes.add("timelogs_missing_on_complete");
  } else if (complete && completionTimelogsIncomplete) {
    confidenceReasonCodes.add("timelogs_incomplete_on_complete");
  }
  if (staleTimelogCoverage) {
    confidenceReasonCodes.add("timelog_stale");
  }
  if (missingRateCoverage) {
    confidenceReasonCodes.add("missing_rate_data");
  }

  const confidenceReasons = Array.from(confidenceReasonCodes).map(makeConfidenceReason);
  let financeConfidenceScore = 100;
  for (const code of confidenceReasonCodes) {
    if (code === "department_average_only") financeConfidenceScore -= 35;
    if (code === "assigned_artist_no_timelogs") financeConfidenceScore -= 15;
    if (code === "timelogs_partial") financeConfidenceScore -= 10;
    if (code === "logging_gap_assumed") financeConfidenceScore -= 25;
    if (code === "timelogs_missing_on_complete") financeConfidenceScore -= 55;
    if (code === "timelogs_incomplete_on_complete") financeConfidenceScore -= 40;
    if (code === "timelog_stale") financeConfidenceScore -= 15;
    if (code === "missing_rate_data") financeConfidenceScore -= 30;
  }
  financeConfidenceScore = Math.max(5, Math.min(100, financeConfidenceScore));

  let financeReliability: FinanceDataReliability = "high";
  if (financeConfidenceScore < 50) {
    financeReliability = "low";
  } else if (financeConfidenceScore < 80) {
    financeReliability = "medium";
  }

  const now = context.now || new Date().toISOString();
  const finalActualSnapshot =
    financialStage === "final_actual"
      ? getSnapshotForStage(stageSnapshots, "final_actual")
      : currentState?.finalActualSnapshot;

  const forecastLabourCost = roundCurrency(
    financialStage === "initial_bid_forecast"
      ? initialSnapshot?.labourCost
      : financialStage === "assigned_artist_forecast"
        ? assignedSnapshot?.labourCost || initialSnapshot?.labourCost
        : typeof loggingGapFallbackLabourCost === "number"
          ? loggingGapFallbackLabourCost
          : liveForecastLabourCost
  );
  const forecastGrossProfit = roundCurrency(
    typeof taskBidValueInMoney === "number" && typeof forecastLabourCost === "number" ? taskBidValueInMoney - forecastLabourCost : undefined
  );
  const fallbackActualsUsed = (complete && completionTimelogsIncomplete) || liveLoggingGap;
  const forecastPresentationMode = getForecastPresentationMode(task, financialStage, actualLoggedMinutes);
  const actualPresentationMode = getActualPresentationMode(financialStage, fallbackActualsUsed);
  const forecastPresentation = buildPresentationText(forecastPresentationMode);
  const actualPresentation = buildPresentationText(actualPresentationMode);

  return {
    taskBidValueInMoney: roundCurrency(taskBidValueInMoney),
    bidTimeAllocationMinutes: bidMinutes,
    bidTimeAllocationDays: roundDays(bidDays),
    departmentAverageDailyRate: roundCurrency(departmentAverageDailyRate),
    assignedArtistDailyRate: roundCurrency(assignedArtistDailyRate),
    assignedArtistIds: task.assignedArtistIds || [],
    actualLoggedMinutes,
    actualLoggedDays: roundDays(actualLoggedDays),
    financialStage,
    forecastLabourCost,
    actualLabourCost: roundCurrency(
      typeof loggingGapFallbackLabourCost === "number" ? loggingGapFallbackLabourCost : actualLabourCost
    ),
    forecastGrossProfit,
    actualGrossProfit: roundCurrency(
      financialStage === "in_progress_on_target" || financialStage === "in_progress_over_schedule" || financialStage === "final_actual"
        ? currentSnapshot?.grossProfit
        : undefined
    ),
    varianceVsBidMinutes,
    varianceVsBidCost: roundCurrency(varianceVsBidCost),
    marginDeltaFromPreviousStage: roundMargin(marginDeltaFromPreviousStage),
    financeReliability,
    financeConfidenceScore,
    confidenceReasons,
    missingRateCoverage,
    missingTimelogCoverage: missingTimelogCoverage || liveLoggingGap,
    fallbackActualsUsed,
    forecastPresentationMode,
    actualPresentationMode,
    forecastLabel: forecastPresentation.label,
    forecastHelperText: forecastPresentation.helperText,
    actualLabel: actualPresentation.label,
    actualHelperText: actualPresentation.helperText,
    provisional: financialStage !== "final_actual" || financeReliability !== "high",
    stageSnapshots,
    finalActualSnapshot,
    finalActualLockedAt: financialStage === "final_actual" ? currentState?.finalActualLockedAt || now : currentState?.finalActualLockedAt,
    overScheduleAlertOpen: currentState?.overScheduleAlertOpen || false,
    overScheduleAlertRaisedAt: currentState?.overScheduleAlertRaisedAt,
    financeEvents: currentState?.financeEvents || [],
    stageChangedAt: currentState?.stageChangedAt,
    lastComputedAt: now,
    note:
      (liveLoggingGap
        ? "Logging gap fallback applied: live logs went stale before completion, so labour cost assumes the assigned artist finished the bid allocation."
        : undefined) ||
      confidenceReasons[0]?.helperText ||
      (financeReliability === "low"
        ? "Finance view is missing key rate or timelog data"
        : financeReliability === "medium"
          ? "Finance view is partially derived"
          : undefined),
  } satisfies TaskFinancialState;
}

function makeFinanceEvent(args: {
  taskId: string;
  eventType: TaskFinanceEvent["eventType"];
  trigger: TaskFinanceTrigger;
  createdAt: string;
  summary: string;
  previousStage?: TaskFinancialStage;
  nextStage?: TaskFinancialStage;
  alertOpen?: boolean;
}) {
  const { taskId, eventType, trigger, createdAt, summary, previousStage, nextStage, alertOpen } = args;
  return {
    id: `${taskId}:${eventType}:${trigger}:${createdAt}`,
    taskId,
    eventType,
    trigger,
    previousStage,
    nextStage,
    summary,
    alertOpen,
    createdAt,
  } satisfies TaskFinanceEvent;
}

export function hasMaterialFinanceChange(previousState: TaskFinancialState | undefined, nextState: TaskFinancialState) {
  if (!previousState) return true;
  return (
    previousState.financialStage !== nextState.financialStage ||
    previousState.overScheduleAlertOpen !== nextState.overScheduleAlertOpen ||
    previousState.finalActualLockedAt !== nextState.finalActualLockedAt ||
    previousState.actualLoggedMinutes !== nextState.actualLoggedMinutes ||
    previousState.actualLabourCost !== nextState.actualLabourCost ||
    previousState.forecastLabourCost !== nextState.forecastLabourCost ||
    previousState.actualGrossProfit !== nextState.actualGrossProfit ||
    previousState.forecastGrossProfit !== nextState.forecastGrossProfit ||
    previousState.varianceVsBidMinutes !== nextState.varianceVsBidMinutes ||
    previousState.varianceVsBidCost !== nextState.varianceVsBidCost
  );
}

export function updateTaskFinancialStage(
  task: Task,
  trigger: TaskFinanceTrigger,
  context: TaskFinanceStageContext = {},
  previousState?: TaskFinancialState
): TaskFinanceStageResolution {
  const now = context.now || new Date().toISOString();
  const nextState = buildBaseTaskFinancialState(task, { ...context, now }, previousState);
  const previousStage = previousState?.financialStage;
  const nextStage = nextState.financialStage;
  const events: TaskFinanceEvent[] = [...(previousState?.financeEvents || [])];
  const newEvents: TaskFinanceEvent[] = [];

  if (previousStage !== nextStage) {
    nextState.stageChangedAt = now;
    newEvents.push(
      makeFinanceEvent({
        taskId: task.id,
        eventType: "stage_changed",
        trigger,
        createdAt: now,
        previousStage,
        nextStage,
        summary: `Task moved from ${previousStage || "untracked"} to ${nextStage}.`,
      })
    );
  } else {
    nextState.stageChangedAt = previousState?.stageChangedAt;
  }

  const shouldRaiseOverScheduleAlert =
    nextStage === "in_progress_over_schedule" &&
    previousState?.overScheduleAlertOpen !== true;

  if (shouldRaiseOverScheduleAlert) {
    nextState.overScheduleAlertOpen = true;
    nextState.overScheduleAlertRaisedAt = now;
    newEvents.push(
      makeFinanceEvent({
        taskId: task.id,
        eventType: "over_schedule_alert",
        trigger,
        createdAt: now,
        previousStage,
        nextStage,
        alertOpen: true,
        summary: "Task has gone over schedule because logged time exceeded the bid time allocation.",
      })
    );
  } else if (nextStage !== "in_progress_over_schedule" && previousState?.overScheduleAlertOpen) {
    nextState.overScheduleAlertOpen = false;
  }

  if (nextStage === "final_actual" && !previousState?.finalActualLockedAt && nextState.finalActualSnapshot) {
    newEvents.push(
      makeFinanceEvent({
        taskId: task.id,
        eventType: "final_actual_locked",
        trigger,
        createdAt: now,
        previousStage,
        nextStage,
        summary: "Task reached Final Actual and its actual labour cost snapshot was locked for reporting.",
      })
    );
  }

  nextState.financeEvents = [...events, ...newEvents];

  return {
    nextState,
    events: newEvents,
    alertsRaised: newEvents.filter((event) => event.eventType === "over_schedule_alert"),
    stageChanged: previousStage !== nextStage,
    shouldRecomputeRollups: hasMaterialFinanceChange(previousState, nextState),
  };
}

export function handleTaskAssignmentFinanceUpdate(task: Task, context: TaskFinanceStageContext = {}, previousState?: TaskFinancialState) {
  return updateTaskFinancialStage(task, "assignment_updated", context, previousState);
}

export function handleTaskStatusFinanceUpdate(task: Task, context: TaskFinanceStageContext = {}, previousState?: TaskFinancialState) {
  return updateTaskFinancialStage(task, "status_updated", context, previousState);
}

export function handleTaskTimelogFinanceUpdate(task: Task, context: TaskFinanceStageContext = {}, previousState?: TaskFinancialState) {
  return updateTaskFinancialStage(task, "timelog_updated", context, previousState);
}

export function handleTaskCompletionFinanceUpdate(task: Task, context: TaskFinanceStageContext = {}, previousState?: TaskFinancialState) {
  return updateTaskFinancialStage(task, "task_completed", context, previousState);
}

export function computeInitialTaskFinancialState(task: Task, context: TaskFinanceStageContext = {}, previousState?: TaskFinancialState) {
  return updateTaskFinancialStage(task, "initial_load", context, previousState);
}

/**
 * Finance rollups should only be recalculated for the parent chain touched by a material task change.
 * This keeps downstream recomputes bounded to the affected project/episode/sequence/shot/asset branch
 * and avoids loops caused by re-running rollups on every no-op task sync.
 */
export function buildTaskFinanceRollupRecalculationPlan(
  task: Task,
  resolution: Pick<TaskFinanceStageResolution, "shouldRecomputeRollups">
): TaskFinanceRollupRecalculationPlan {
  const unique = (value?: string) => (value ? [value] : []);

  return {
    shouldRecompute: resolution.shouldRecomputeRollups,
    projectIds: resolution.shouldRecomputeRollups ? unique(task.projectId) : [],
    episodeIds: resolution.shouldRecomputeRollups ? unique(task.episodeId) : [],
    sequenceIds: resolution.shouldRecomputeRollups ? unique(task.sequenceId) : [],
    shotIds: resolution.shouldRecomputeRollups ? unique(task.shotId || (task.parentEntityType === "shot" ? task.parentEntityId : undefined)) : [],
    assetIds: resolution.shouldRecomputeRollups ? unique(task.assetId || (task.parentEntityType === "asset" ? task.parentEntityId : undefined)) : [],
  };
}

// TODO(finance): feed approved leave / sickness / holiday availability into task-stage confidence so
// staffing assumptions can distinguish "artist missing" from "artist temporarily unavailable".
// TODO(finance): enrich actual labour cost with external payroll / finance sources only as additive inputs;
// do not replace the current deterministic rate-and-timelog fallback rules.
// TODO(finance): extend stage snapshots for richer forecasting, departmental budgeting, and approval
// workflow checkpoints without changing the existing stage names or current downstream contracts.
